import mpiHelper from '@/helpers/mpiHelper';
import mpiFieldMapper from '@/helpers/mpiFieldMapper';
import vars from '@/config/vars';
import PaymentTypes from '@/models/PaymentTypes';
import CardBrands from '@/models/CardBrands';
import MpiPaymentMethods from '@/models/MpiPaymentMethods';
import store from '@/store';

export default {
    namespaced: true,

    state: {
        Accesstoken: null,
        Cavv: null,
        Xid: null,
        Eci: null,
        Version: null,
        ReferenceID: null,
        ReturnCode: null,
        ReturnMessage: null,
        DataOnly: null,
        Environment: 'SDB',
        Debug: true,
        CreditAuthenticationEnabled: false,
        DebitAuthenticationEnabled: false,
        MpiIsReady: false,
        MpiPromiseCallback: {
            resolve: null,
            reject: null
        },
        multiLanguage: null,
        AutomaticCancellationWhenUnauthenticated: false,
        AffiliationCodeAmex: null
    },

    mutations: {
        /**
         * Mapeia o response da api para o módulo
         */
        setFromRequest(state, data) {
            state.Accesstoken = data.AccessTokenMpiVersionTwo;
            state.DataOnly = data.MpiDataOnly;
            state.Environment = data.EnvironmentToMpi;
            state.Debug = data.DebugMpiScript;
            state.CreditAuthenticationEnabled = data.MpiCreditAuthenticationEnabled;
            state.DebitAuthenticationEnabled = data.MpiDebitAuthenticationEnabled;
            state.AutomaticCancellationWhenUnauthenticated = data.AutomaticCancellationWhenUnauthenticated;
            state.AffiliationCodeAmex = data.AffiliationCodeAmex;
        },

        setCavv: (state, value) => state.Cavv = value,
        setXid: (state, value) => state.Xid = value,
        setEci: (state, value) => state.Eci = value,
        setVersion: (state, value) => state.Version = value,
        setReferenceID: (state, value) => state.ReferenceID = value,
        setReturnCode: (state, value) => state.ReturnCode = value,
        setReturnMessage: (state, value) => state.ReturnMessage = value,
        setMpiIsReady: (state, value) => state.MpiIsReady = value
    },

    getters: {
        /**
         * Monta o contrato de submit
         */
        getSubmitModel(state, getters, rootState) {
            return {
                ExternalAuthentication: {
                    Cavv: state.Cavv,
                    Xid: state.Xid,
                    Eci: state.Eci,
                    Version: state.Version,
                    ReferenceID: state.ReferenceID,
                    DataOnly: state.DataOnly,
                },
                MpiVersionTwoIsEnabled: getters['shouldAuthenticate'],
                MpiDataOnlyIsEnabled: getters['shouldUseDataOnly']
            };
        },

        shouldAuthenticate(state, getters, rootState) {
            const cardBrand = store.getters['payment/getSelectedCardBrand'];
            const isAmex = cardBrand === CardBrands.AMEX;

            let activePaymentType = store.state.payment.PaymentType;

            let shouldUseMpi = (activePaymentType == PaymentTypes.CREDIT_CARD ||
                               activePaymentType == PaymentTypes.DEBIT_CARD);

            if (shouldUseMpi === false)
                return false;

            if(isAmex && !state.AffiliationCodeAmex)
                return false;

            let shouldAuthenticate = state.CreditAuthenticationEnabled && activePaymentType == PaymentTypes.CREDIT_CARD
                                     || activePaymentType == PaymentTypes.DEBIT_CARD;

            if(shouldAuthenticate && state.AutomaticCancellationWhenUnauthenticated == true)
                return true;

            return shouldAuthenticate && paymentMethodIsAuthenticable(rootState.payment.PaymentMethodId);
        },

        shouldUseDataOnly(state, getters, rootState) {
            let dataOnlyIsEnabled = state.DataOnly;
            let shouldAuthenticate = getters['shouldAuthenticate'];
            let brandCanUseDataOnly = paymentMethodCanUseDataOnly(rootState.payment.PaymentMethodId);

            if(dataOnlyIsEnabled &&
                shouldAuthenticate == false &&
                state.AutomaticCancellationWhenUnauthenticated == true)
                return true;

            return dataOnlyIsEnabled
                    && shouldAuthenticate == false
                    && brandCanUseDataOnly;
        },

        GetAutomaticCancellationWhenUnauthenticated(state, getters, rootState) {
            return state.AutomaticCancellationWhenUnauthenticated;
        }
    },

    actions: {
        /**
         * Mapeia se é dataonly ou não e inicia a autenticação.
         */
        async authenticate({ state, dispatch, rootState, getters }) {
            if (getters['shouldAuthenticate'])
                mpiHelper.addMpiField('bpmpi_auth_notifyonly', 'false');
            else if(getters['shouldUseDataOnly'])
                mpiHelper.addMpiField('bpmpi_auth_notifyonly', 'true');
            else
                return;

            await dispatch('initMpiProcess');
        },

        /**
         * Inicia o processo de autenticação junto ao MPI.
         */
        initMpiProcess({ state, rootState, rootGetters }) {
            const cardBrand = store.getters['payment/getSelectedCardBrand'];
            const isAmex = cardBrand === CardBrands.AMEX;

            return new Promise((resolve, reject) => {
                mpiHelper.addMpiField('bpmpi_auth', 'true');
                mpiHelper.addMpiField('bpmpi_accesstoken', state.Accesstoken);

                if(isAmex) {
                    mpiHelper.addMpiField('bpmpi_brand_establishment_code', state.AffiliationCodeAmex);
                }

                // Map MPI fields
                mpiFieldMapper.mapOrderData(rootState, rootGetters);
                mpiFieldMapper.mapOrderDetails(rootState, rootGetters);
                mpiFieldMapper.mapDeviceData(rootState);
                mpiFieldMapper.mapBillingAddress(rootState);
                mpiFieldMapper.mapShippingAddress(rootState);
                mpiFieldMapper.mapCartItems(rootState);
                mpiFieldMapper.mapUserData(rootState);
                mpiFieldMapper.mapEnrollmentData();

                state.MpiPromiseCallback = {
                    resolve,
                    reject
                };

                if (state.MpiIsReady == false)
                {
                    // Função de configuração do MPI
                    mpiHelper.setMpiCallbacks();

                    // Importa o script do MPI de forma dinâmica
                    let mpiScript = document.createElement('script');
                    mpiScript.src = vars.MPI_SCRIPT_URL;
                    document.head.appendChild(mpiScript);
                }
                else {
                    bpmpi_authenticate();
                }
            });
        }
    }
};

/**
 * Verifica se um determinado meio de pagamento é autenticável.
 * O arquivo MpiPaymentMethods contém as configurações
 * @param {Number} paymentMethod Código do meio de pagamento
 */
function paymentMethodIsAuthenticable(paymentMethod) {
    let supportedMethods = MpiPaymentMethods.AUTHENTICATION.DEV;

    if (vars.ENVIRONMENT == 'PRD')
        supportedMethods = MpiPaymentMethods.AUTHENTICATION.PRD;
    else if (vars.ENVIRONMENT == 'SDB')
        supportedMethods = MpiPaymentMethods.AUTHENTICATION.SDB;

    let paymentMethodId = parseInt(paymentMethod, 10);

    return supportedMethods.indexOf(paymentMethodId) !== -1;
};

/**
 * Verifica se um determinado meio de pagamento pode passar dataonly.
 * O arquivo MpiPaymentMethods contém as configurações
 * @param {Number} paymentMethod Código do meio de pagamento
 */
function paymentMethodCanUseDataOnly(paymentMethod) {
    let supportedMethods = MpiPaymentMethods.DATAONLY.DEV;

    if (vars.ENVIRONMENT == 'PRD')
        supportedMethods = MpiPaymentMethods.DATAONLY.PRD;
    else if (vars.ENVIRONMENT == 'SDB')
        supportedMethods = MpiPaymentMethods.DATAONLY.SDB;

    let paymentMethodId = parseInt(paymentMethod, 10);

    return supportedMethods.indexOf(paymentMethodId) !== -1;
};
